/**
 * ThankYou Component
 * 
 * Final confirmation page shown after funnel completion.
 * Features:
 * - Success animation (confetti/celebration)
 * - Summary of key responses
 * - Next steps messaging
 * - Option to restart or share
 */

import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card } from '../UI/Card';
import { Button } from '../UI/Button';
import { Logo } from '../UI/Logo';
import { useFunnel } from '../../context/FunnelContext';
import { trackFunnelCompleted, calculateTimeSpent } from '../../utils/analytics';

// ============================================
// TYPES
// ============================================

interface ThankYouProps {
  /** Handler to restart the funnel */
  onRestart?: () => void;
}

// ============================================
// COMPONENT
// ============================================

/**
 * ThankYou - Celebration and confirmation page
 */
export const ThankYou: React.FC<ThankYouProps> = ({ onRestart }) => {
  const { state, resetFunnel } = useFunnel();
  const [showConfetti, setShowConfetti] = useState(true);

  // Track completion and trigger confetti
  useEffect(() => {
    // Track funnel completion
    if (state.startedAt) {
      const totalTime = calculateTimeSpent(state.startedAt);
      trackFunnelCompleted(state, totalTime);
    }

    // Hide confetti after animation
    const timer = setTimeout(() => {
      setShowConfetti(false);
    }, 3000);

    return () => clearTimeout(timer);
  }, [state]);

  // Handle restart
  const handleRestart = () => {
    resetFunnel();
    if (onRestart) {
      onRestart();
    }
  };

  // Get personalized info from answers
  const userName = state.answers['contact-info'] 
    ? (state.answers['contact-info'] as Record<string, string>).name 
    : '';
  const userEmail = state.answers['contact-info']
    ? (state.answers['contact-info'] as Record<string, string>).email
    : '';

  return (
    <div className="w-full max-w-question mx-auto px-4 sm:px-6 py-8">
      {/* Confetti Animation */}
      <AnimatePresence>
        {showConfetti && <ConfettiAnimation />}
      </AnimatePresence>

      {/* Logo at top - overlapping the card */}
      <div className="relative z-20 -mb-24 w-full">
        <Logo size="lg" animate={true} />
      </div>

      {/* Main Card */}
      <Card className="bg-white/95 backdrop-blur-sm shadow-2xl ring-4 ring-white/30 pt-28">
        {/* Success Icon */}
        <motion.div
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{
            type: 'spring',
            stiffness: 200,
            damping: 15,
            delay: 0.2,
          }}
          className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-secondary to-secondary-400 flex items-center justify-center shadow-lg"
        >
          <CheckmarkIcon />
        </motion.div>

        {/* Heading */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="text-center mb-8"
        >
          <h1 className="text-3xl sm:text-4xl font-bold text-text-primary font-heading mb-3">
            {userName ? `Thank You, ${userName}!` : 'Thank You!'}
          </h1>
          <p className="text-lg text-text-secondary">
            Your personalized AI automation plan is on its way.
          </p>
        </motion.div>

        {/* What to expect */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-surface rounded-xl p-6 mb-8"
        >
          <h2 className="font-semibold text-text-primary mb-4 flex items-center gap-2">
            <EmailIcon />
            What Happens Next?
          </h2>
          <ul className="space-y-3">
            <NextStep
              number={1}
              text={`Check your inbox at ${userEmail || 'your email'} for your report`}
            />
            <NextStep
              number={2}
              text="Review your customized AI automation recommendations"
            />
            <NextStep
              number={3}
              text="Schedule a free consultation to discuss implementation"
            />
          </ul>
        </motion.div>

        {/* Response Summary */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="border-t border-border pt-6"
        >
          <h3 className="text-sm font-medium text-text-secondary uppercase tracking-wide mb-4">
            Your Response Summary
          </h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <SummaryItem
              label="Service Type"
              value={getServiceTypeLabel(state.serviceType)}
            />
            <SummaryItem
              label="Primary Goal"
              value={getGoalLabel(state.answers['micro-goals'] as string)}
            />
          </div>
        </motion.div>

        {/* Actions */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="mt-8 flex flex-col sm:flex-row gap-3 justify-center"
        >
          <Button
            variant="secondary"
            onClick={handleRestart}
          >
            Start Over
          </Button>
        </motion.div>
      </Card>

      {/* Bottom message */}
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
        className="text-center text-sm text-text-secondary mt-6"
      >
        Questions? Email us at{' '}
        <a
          href="mailto:support@example.com"
          className="text-primary hover:underline"
        >
          support@example.com
        </a>
      </motion.p>
    </div>
  );
};

ThankYou.displayName = 'ThankYou';

// ============================================
// SUBCOMPONENTS
// ============================================

interface NextStepProps {
  number: number;
  text: string;
}

const NextStep: React.FC<NextStepProps> = ({ number, text }) => (
  <li className="flex items-start gap-3">
    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-white text-sm font-bold flex items-center justify-center">
      {number}
    </span>
    <span className="text-text-secondary">{text}</span>
  </li>
);

interface SummaryItemProps {
  label: string;
  value: string;
}

const SummaryItem: React.FC<SummaryItemProps> = ({ label, value }) => (
  <div>
    <dt className="text-text-secondary text-xs">{label}</dt>
    <dd className="font-medium text-text-primary">{value}</dd>
  </div>
);

// ============================================
// CONFETTI ANIMATION
// ============================================

const ConfettiAnimation: React.FC = () => {
  // Generate random confetti pieces
  const pieces = Array.from({ length: 50 }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    delay: Math.random() * 0.5,
    duration: 2 + Math.random() * 2,
    color: ['#4F46E5', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'][Math.floor(Math.random() * 5)],
    size: 6 + Math.random() * 6,
  }));

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden z-50">
      {pieces.map((piece) => (
        <motion.div
          key={piece.id}
          initial={{
            opacity: 1,
            x: `${piece.x}vw`,
            y: -20,
            rotate: 0,
            scale: 1,
          }}
          animate={{
            opacity: 0,
            y: '100vh',
            rotate: 720,
            scale: 0,
          }}
          transition={{
            duration: piece.duration,
            delay: piece.delay,
            ease: 'easeOut',
          }}
          style={{
            position: 'absolute',
            width: piece.size,
            height: piece.size,
            backgroundColor: piece.color,
            borderRadius: Math.random() > 0.5 ? '50%' : '0%',
          }}
        />
      ))}
    </div>
  );
};

// ============================================
// ICONS
// ============================================

const CheckmarkIcon: React.FC = () => (
  <svg
    className="w-10 h-10 text-white"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={3}
  >
    <motion.path
      initial={{ pathLength: 0 }}
      animate={{ pathLength: 1 }}
      transition={{ duration: 0.5, delay: 0.5 }}
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M5 13l4 4L19 7"
    />
  </svg>
);

const EmailIcon: React.FC = () => (
  <svg className="w-5 h-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
    />
  </svg>
);

// ============================================
// HELPER FUNCTIONS
// ============================================

function getServiceTypeLabel(serviceType?: string): string {
  const labels: Record<string, string> = {
    home: 'Home Services',
    personal: 'Personal Services',
    professional: 'Professional Services',
    maintenance: 'Maintenance & Repair',
  };
  return labels[serviceType || ''] || 'Not specified';
}

function getGoalLabel(goal?: string): string {
  const labels: Record<string, string> = {
    scaling: 'Scale Business',
    efficiency: 'Improve Efficiency',
    'customer-service': 'Better Customer Service',
    revenue: 'Increase Revenue',
    other: 'Custom Goal',
  };
  return labels[goal || ''] || 'Not specified';
}

export default ThankYou;
